import { createRouter, createWebHistory } from "vue-router";
import { routes } from "vue-router/auto-routes";
import { consumeLockedOnLoad, rehydrateSession, useSession } from "@/features/auth";
import { ALLOW, HOME, type StaffRole, type Workspace, can } from "@/shared";

/**
 * Where each workspace lives. One segment per workspace, and the feature folder that serves it is
 * named the same — see `vite.config.ts`, where the route tree is generated from
 * `src/features/<name>/routes/`.
 */
export const WORKSPACE_PATH = {
  kiosks: "/kiosks",
  cage: "/cage",
  day: "/day",
  tenancy: "/tenancy",
  activity: "/activity",
} satisfies Record<Workspace, string>;

/**
 * Which roles may enter a workspace, as data rather than conditionals.
 *
 * Each entry names the allow-list the API enforces on that workspace's *primary* route, so a drift
 * between this table and the service shows up as one wrong door rather than a scattering of 403s.
 * `/day` is `reconcile` and not `signoff`: a cashier reads the reconciliation report and cannot
 * sign it, and gating the door on the stricter of the two would hide the report from someone the
 * API is happy to show it to. Sign-off is disabled *within* the workspace, with its reason — the
 * convention is to hide a section a role can never reach and disable an action within one it can.
 */
const WORKSPACE_ALLOW = {
  "/kiosks": ALLOW.listKiosks,
  "/cage": ALLOW.cageWithdrawal,
  "/day": ALLOW.reconcile,
  "/tenancy": ALLOW.casinoAdmin,
  "/activity": ALLOW.activityRead,
} satisfies Record<(typeof WORKSPACE_PATH)[Workspace], readonly StaffRole[]>;

/**
 * The workspace segment a path belongs to. `/day/signoff` and `/day/` both belong to `/day` — the
 * granularity `WORKSPACE_ALLOW` is keyed at — because a workspace owns everything under it, not
 * just its own root.
 *
 * `path` always starts with `/` (that is what a resolved route's `to.path` is), so splitting on
 * `/` puts the workspace name at index 1 regardless of what follows it — `"day"` from
 * `/day/signoff` and, for free, from a bookmarked trailing-slash `/day/` too: vue-router's
 * non-strict matcher treats `/day` and `/day/` as the same route, but `to.path` in the guard keeps
 * whichever one the visitor actually typed, and index-based extraction does not care either way.
 */
function workspaceSegment(path: string): string | null {
  const segment = path.split("/")[1];
  return segment ? `/${segment}` : null;
}

/**
 * The role guard, pure over `(role, path)`.
 *
 * Exported separately from the router and taking no router, no store and no DOM: a guard testable
 * only through a mounted router is a guard whose test needs a component tree, and this one encodes
 * ADR-0005's duty split, which is the part worth testing directly.
 *
 * **Matches on the owning workspace, not the exact path.** `WORKSPACE_ALLOW` names four workspace
 * roots, but a workspace's real routes nest under them — Task 7's `/tenancy/staff`, Task 6's
 * `/day/signoff`, any `/kiosks/:id` drilldown. Looking up `path` verbatim bounced every one of
 * those for a role that could plainly use the workspace they belong to; `workspaceSegment` resolves
 * `path` to the root first, so the table only ever needs the four it already has.
 *
 * **Unknown workspaces are refused for everyone, `platform_admin` included.** The lookup cannot
 * fall back to an empty list, because an empty list already means something here —
 * `ALLOW.casinoAdmin` is `[]`, meaning admin-only — so a deleted or mistyped route would quietly
 * become admin-reachable instead of unreachable.
 */
export function canEnter(role: StaffRole, path: string): boolean {
  const workspace = workspaceSegment(path);
  const allowed =
    workspace !== null && Object.hasOwn(WORKSPACE_ALLOW, workspace)
      ? WORKSPACE_ALLOW[workspace as keyof typeof WORKSPACE_ALLOW]
      : null;
  return allowed !== null && can(role, allowed);
}

/** Nav order. Not alphabetical — it is the order a shift actually touches these in. `activity`
 *  sits last because nothing on a shift routes THROUGH it: it is read after the fact, by the one
 *  role whose own actions it records. */
export const WORKSPACE_ORDER: Workspace[] = ["kiosks", "cage", "day", "tenancy", "activity"];

/**
 * The workspaces that stay in `platform_admin`'s permanent nav outside break-glass.
 *
 * Both are Casino-LESS surfaces — they answer across the whole platform rather than inside one
 * Casino — which is exactly what makes them safe to show permanently: neither one's calls carry a
 * `casinoId`, so neither depends on a mode being entered first. Every other workspace is a cash
 * workspace scoped to one Casino, and the design record keeps those out of a superset menu
 * (`visibleWorkspaces` below).
 */
const ADMIN_PERMANENT: readonly Workspace[] = ["tenancy", "activity"];

/**
 * Which workspaces belong in `AppShell.vue`'s persistent nav for a role — usability layered on
 * top of `canEnter`, and for one role it narrows rather than mirrors it.
 *
 * `canEnter` passes `platform_admin` on every workspace, correctly: ADR-0005's amendment really
 * does let admin perform any operation at any Casino, for cover and emergencies. But the design
 * record (`BACKOFFICE-FRONTEND-DESIGN.md` §2, and the `HOME` comment in `roles.ts`) is explicit
 * that those cash workspaces are "entered through break-glass rather than sitting in a superset
 * menu" — the permanent nav should show `ADMIN_PERMANENT` alone until admin has explicitly entered
 * a Casino. `isBreakGlassActive` is that signal. Every other role's nav is exactly its `canEnter`
 * filter, unchanged.
 *
 * The narrowing is keyed on `ADMIN_PERMANENT`, not on `w !== "tenancy"`: `activity` is admin-only
 * and Casino-less like tenancy, and hiding it behind break-glass would put the platform-wide
 * attribution trail behind the very mode it exists to record.
 *
 * Exported and pure over `(role, isBreakGlassActive)`, not read out of `AppShell.vue`'s template,
 * for the same reason `canEnter` is separate from the router: a nav rule tested only by mounting
 * the shell is a rule whose test needs a DOM, a router, i18n and two stores before it can see one
 * `if`.
 */
export function visibleWorkspaces(role: StaffRole, isBreakGlassActive: boolean): Workspace[] {
  return WORKSPACE_ORDER.filter((w) => {
    if (!canEnter(role, WORKSPACE_PATH[w])) return false;
    if (role === "platform_admin" && !ADMIN_PERMANENT.includes(w)) return isBreakGlassActive;
    return true;
  });
}

/** Routes reachable without a session. Everything else needs one. */
const PUBLIC_PATHS = new Set(["/login"]);

export const router = createRouter({ history: createWebHistory(), routes });

/**
 * One guard, five decisions, in the order that keeps them from fighting each other.
 *
 * The role checks read `useSession()`, which is `GET /v1/me`'s answer and never the token — the
 * token carries `{staffId}` alone and decoding it would be making an authorization decision the
 * API has already made differently. **None of this is enforcement.** The API re-resolves role and
 * Casino on every request; this only decides which door to open, so that a role never lands on a
 * screen whose every call would 403.
 *
 * **`await`ing `rehydrateSession()` here, before `isAuthed` is read, is the whole fix for a reload
 * losing the operator's place.** A reload starts with an empty Pinia store — `isAuthed` reads
 * `false` even for someone mid-shift a second ago — so deciding on that stale value would redirect
 * to `/login` before the persisted token even gets a chance to prove itself, and nothing re-runs
 * this guard once `/v1/me` answers a moment later. Awaiting first means this evaluation already
 * reflects the true state, in one hop — no second redirect, no `/login` special-case papering over
 * the gap. `rehydrateSession()` itself is a no-op when there is nothing stored and memoized after
 * its first real attempt, so this costs nothing on the navigations that follow.
 */
router.beforeEach(async (to) => {
  const session = useSession();

  if (!session.isAuthed) {
    await rehydrateSession();
  }

  if (!session.isAuthed) {
    // Read once, whatever the destination: a stale flag must not surface on a later bounce.
    const idleLocked = consumeLockedOnLoad();
    if (PUBLIC_PATHS.has(to.path)) return true;
    // A reload after the idle window: the stored token was refused unread (`idle-lock.ts`), and
    // `/login` should say why rather than look like a random sign-out.
    if (idleLocked) return { path: "/login", query: { redirect: to.fullPath, reason: "idle" } };
    // `redirect` is `login.vue`'s own signal for "come back here after signing in" — the same
    // "don't lose the operator's place" property `9480006` gave a reload, extended to a login that
    // followed a bounce. `to.fullPath`, not `to.path`: a bookmarked `/day/signoff?…` should return
    // with its query intact too.
    return { path: "/login", query: { redirect: to.fullPath } };
  }

  // `role` is non-null whenever `isAuthed` is, but the store types them independently.
  const role = session.role;
  if (!role) return { path: "/login" };

  const home = WORKSPACE_PATH[HOME[role]];

  // A signed-in person has no use for the login screen, and leaving it reachable lets a second
  // login overwrite a live session from a bookmark.
  if (PUBLIC_PATHS.has(to.path)) return { path: home };
  if (to.path === "/") return { path: home };

  // Hide, don't explain: a workspace a role can never reach is not in their navigation either, so
  // arriving here means a typed URL or a stale bookmark. Send them where they belong.
  return canEnter(role, to.path) ? true : { path: home };
});
